import React, { useMemo, useState } from 'react'
import type { ColumnFiltersState } from '@tanstack/react-table'
import type { ColumnDefinition } from '../Table/types'
import { FilterToolbarChip } from '../Table/components/FilterToolbarChip'
import { columnFiltersToChipState } from '../Table/columnAdapter'
import { Modal } from '../Modal'
import { Button } from '../Button'
import { getPortalContainer } from '../../lib/portalContainer'
import { useT } from '../../../app/lib/i18n'
import * as s from './ListView.css'
import * as ts from '../Table/Table.css'

export interface FiltersSheetModalProps<TRow> {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** All filterable columns — used with visibleFilterIds to resolve selected quick filters. */
  allFilterColumns: ColumnDefinition<TRow>[]
  visibleFilterIds: string[]
  columnFilters: ColumnFiltersState
  rows: TRow[]
  onChipFilterChange: (colId: string, values: string[] | null) => void
  sortableColumns: ColumnDefinition<TRow>[]
  sort: { sort_by: string; order: 'asc' | 'desc' } | undefined
  onSortChange: (sort: { sort_by: string; order: 'asc' | 'desc' }) => void
  showCounts?: boolean
}

export function FiltersSheetModal<TRow>({
  open,
  onOpenChange,
  allFilterColumns,
  visibleFilterIds,
  columnFilters,
  rows,
  onChipFilterChange,
  sortableColumns,
  sort,
  onSortChange,
  showCounts = false,
}: FiltersSheetModalProps<TRow>) {
  const t = useT()
  const chipFilters = useMemo(
    () => columnFiltersToChipState(columnFilters),
    [columnFilters],
  )
  const [sortMenuOpen, setSortMenuOpen] = useState(false)

  const visibleSet = useMemo(() => new Set(visibleFilterIds), [visibleFilterIds])

  const selectedFilters = useMemo(
    () => allFilterColumns.filter((c) => visibleSet.has(c.id)),
    [allFilterColumns, visibleSet],
  )

  const currentSortCol = sortableColumns.find(
    (c) => (c.sortKey ?? c.id) === sort?.sort_by,
  )
  const currentSortLabel = currentSortCol
    ? (currentSortCol.header ??
      (typeof currentSortCol.label === 'string'
        ? currentSortCol.label
        : currentSortCol.id))
    : (sort?.sort_by ?? '—')

  return (
    <Modal
      open={open}
      setOpen={onOpenChange}
      minWidth="360px"
      maxWidth="480px"
      minHeight="auto"
      maxHeight="85vh"
      container={getPortalContainer()}
    >
      <Modal.Header>
        <h3 className={s.filtersSheetTitle}>{t('shared.listView.quickFiltersTitle')}</h3>
      </Modal.Header>
      <Modal.Body>
        <div className={s.filtersSheetBody}>
          {sortableColumns.length > 0 ? (
            <div className={s.filtersSheetSort}>
              <div className={s.filtersSheetSectionLabel}>{t('shared.listView.sort')}</div>
              <button
                type="button"
                className={`${ts.sortConfigBtn} ${ts.filterSheetTrigger}${sortMenuOpen ? ' is-active' : ''}`}
                onClick={() => setSortMenuOpen((o) => !o)}
                aria-expanded={sortMenuOpen}
                aria-label={`Sort by: ${currentSortLabel}`}
              >
                <svg
                  width={14}
                  height={14}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M6 9l6-6 6 6M6 15l6 6 6-6" />
                </svg>
                <span>{currentSortLabel}</span>
                {sort ? (
                  <svg
                    width={11}
                    height={11}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.25}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    {sort.order === 'desc' ? (
                      <path d="M12 5v14M5 12l7 7 7-7" />
                    ) : (
                      <path d="M12 19V5M5 12l7-7 7 7" />
                    )}
                  </svg>
                ) : null}
              </button>
              {sortMenuOpen ? (
                <div className={s.filtersSheetSortMenu} role="listbox">
                  {sortableColumns.map((col) => {
                    const colKey = col.sortKey ?? col.id
                    const colLabel =
                      col.header ??
                      (typeof col.label === 'string' ? col.label : col.id)
                    const isActive = sort?.sort_by === colKey
                    const isDesc = isActive && sort?.order === 'desc'
                    return (
                      <button
                        key={col.id}
                        type="button"
                        className={s.filtersSheetSortOption}
                        data-active={isActive ? 'true' : undefined}
                        onClick={() => {
                          onSortChange({
                            sort_by: colKey,
                            order: isActive ? (isDesc ? 'asc' : 'desc') : 'asc',
                          })
                          setSortMenuOpen(false)
                        }}
                      >
                        <span>{colLabel}</span>
                        {isActive ? (
                          <svg
                            width={12}
                            height={12}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden
                          >
                            {isDesc ? (
                              <path d="M12 5v14M5 12l7 7 7-7" />
                            ) : (
                              <path d="M12 19V5M5 12l7-7 7 7" />
                            )}
                          </svg>
                        ) : null}
                      </button>
                    )
                  })}
                </div>
              ) : null}
            </div>
          ) : null}

          {selectedFilters.length > 0 ? (
            <div className={s.filtersSheetSection}>
              <div className={s.filtersSheetSectionLabel}>{t('shared.listView.quickFiltersLabel')}</div>
              {selectedFilters.map((col) => (
                <div key={col.id} className={s.filtersSheetRow}>
                  <FilterToolbarChip
                    column={col}
                    allRows={rows}
                    value={chipFilters[col.id] ?? null}
                    onChange={(vs) => onChipFilterChange(col.id, vs)}
                    showCounts={showCounts}
                    presentation="inline"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className={s.filtersSheetEmpty}>
              No quick filters selected. Use Set Filters to choose which filters
              appear here.
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="button"
          color="primary"
          variant="solid"
          fullWidth
          onClick={() => onOpenChange(false)}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
